import React from 'react';

import { createFragmentContainer, QueryRenderer, graphql } from 'react-relay';
import { Link } from 'react-router-dom';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Toast, ToastProvider, ToastViewport } from './ui/snackbar';
import { X } from 'lucide-react';

import QueryError from './QueryError';
import Card from './Card';
import RelayEnv from '../RelayEnv';
import DeleteTeamInvitationMutation from '../mutations/DeleteTeamInvitationMutation';
import CreateTeamInviteMutation from '../mutations/CreateTeamInviteMutation';
import UpdateTeamMutation from '../mutations/UpdateTeamMutation';

function SimpleDialog(props) {
  const { onClose, open } = props;
  const [email, setEmail] = React.useState('');

  let onInvite = e => {
    e.preventDefault();
    CreateTeamInviteMutation.commit(
      email,
      () => {
        onClose();
      },
      () => {
        onClose();
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Team Member</DialogTitle>
          <DialogDescription>
            Please enter the email of a user you'd like to invite to your team.{' '}
            An email <strong>will not</strong> be sent to them. After clicking
            add, you'll need to send them the invite link.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onInvite} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="team-email">Email Address</Label>
            <Input
              id="team-email"
              onChange={e => setEmail(e.target.value)}
              type="email"
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Add</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

let Team = ({ viewer }) => {
  const [open, setOpen] = React.useState(false);
  const [snackOpen, setSnackOpen] = React.useState(false);

  let callbackQ = viewer.team.callbackUrl
    ? viewer.team.callbackUrl
    : 'https://';
  const [callback, setCallback] = React.useState(callbackQ);

  const onOpen = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  let onRemove = id => e => {
    DeleteTeamInvitationMutation.commit(id);
  };

  let onWebhookSave = () => {
    UpdateTeamMutation.commit(callback, null, resp => {
      setSnackOpen(true);
    });
  };

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12">
        <Card className="p-4">
          <h2 className="text-2xl font-semibold mb-2">Team Callback</h2>
          <p className="text-sm mb-4">
            Specify a webhook endpoint to receive a POST request for each event
            on your team. For more information refer to the{' '}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://pushback.io/docs/webhooks"
              className="text-primary underline"
            >
              webhook docs
            </a>
          </p>
          <div className="flex flex-col space-y-2 mb-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="team-webhook">Webhook endpoint</Label>
              <Input
                id="team-webhook"
                onChange={e => setCallback(e.target.value)}
                value={callback}
                className="w-[240px]"
              />
              <Button onClick={onWebhookSave}>Save</Button>
            </div>
          </div>
          {viewer.team.callbackUrl && (
            <Button asChild variant="outline">
              <Link
                to={{
                  pathname: '/team_callback',
                }}
              >
                Customize Webhook
              </Link>
            </Button>
          )}
          <ToastProvider>
            <Toast
              open={snackOpen}
              onOpenChange={isOpen => !isOpen && setSnackOpen(false)}
            >
              <span>Saved!</span>
            </Toast>
            <ToastViewport />
          </ToastProvider>
        </Card>
      </div>
      <div className="col-span-12">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold">New members invites</h2>
            <Button onClick={onOpen}>Add</Button>
            <SimpleDialog open={open} onClose={onClose} />
          </div>
          <p className="text-sm mb-4">
            Send the invite link to users and have them sign in to pushback with
            the matching email to join your team.
          </p>
          {viewer.team.invitations.edges.map(edge => {
            let invite = edge.node;
            return (
              <div key={invite.id} className="flex items-center mb-2">
                <Button
                  variant="ghost"
                  size="icon"
                  id={invite.id}
                  onClick={onRemove(invite.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
                <div className="mr-2"> {invite.email || ''}&nbsp;&nbsp; </div>
                <div className="break-all">
                  <a href={invite.url} className="text-primary underline">
                    {invite.url}
                  </a>
                </div>
              </div>
            );
          })}
        </Card>
      </div>
      <div className="col-span-12">
        <Card className="p-4">
          <h2 className="text-2xl font-semibold mb-4">Team Members</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {viewer.team.users.edges.map(edge => {
                let user = edge.node;
                return (
                  <TableRow key={user.id}>
                    <TableCell>{user.fullName}</TableCell>
                    <TableCell> {user.email || ''}</TableCell>
                    <TableCell> {user.role} </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
};

Team = createFragmentContainer(Team, {
  viewer: graphql`
    fragment Team_viewer on User {
      id
      team {
        id
        callbackQuery
        callbackUrl
        invitations(first: 100) @connection(key: "Team_invitations") {
          edges {
            node {
              url
              email
              id
            }
          }
        }
        users(first: 100) @connection(key: "Team_users") {
          edges {
            node {
              id
              fullName
              email
              role
            }
          }
        }
      }
    }
  `,
});

class TeamView extends React.Component {
  render() {
    return (
      <QueryRenderer
        query={graphql`
          query TeamQuery {
            viewer {
              id
              ...Team_viewer
            }
          }
        `}
        environment={RelayEnv}
        variables={{}}
        render={({ error, retry, props }) => {
          if (error) {
            return <QueryError retry={retry} error={error} />;
          } else if (props) {
            return <Team viewer={props.viewer} {...props} />;
          } else {
            return null;
          }
        }}
      />
    );
  }
}

export default TeamView;
